import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Formik, FormikProps } from "formik";
import { FormControl, FormControlLabel, MenuItem } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Unstable_Grid2";
import TextField from "@mui/material/TextField";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { ANDatePicker } from "@answersnow/pkg-theme";
import { COUNTRIES, STATES, RELATIONSHIPS } from "@constants/index";
import { formatToPhoneNumber } from "utils/formatFormFields";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import * as selectors from "selectors";
import EditUserInfoAlert from "../Utils/EditUserInfoAlert";

/** Exposes an imperative submit method to parent components via ref. */
export interface EditFieldsHandle {
  submit: () => void;
}

/**
 * Renders as null; tracks Formik dirty state and notifies parent via callback.
 * Exists as a named component so the useEffect obeys the Rules of Hooks.
 */
function DirtyTracker({
  dirty,
  onDirtyChange,
}: {
  dirty: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}) {
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty]);
  return null;
}

const validationSchema = yup.object().shape({
  firstName: yup.string(),
  lastName: yup.string(),
  dateOfBirth: yup.date().nullable(),
  gender: yup.string(),
  relationshipToClient: yup.string(),
  phone: yup.string(),
  email: yup.string().email("Invalid email"),
  address1: yup.string(),
  address2: yup.string(),
  country: yup.string(),
  state: yup.string(),
  city: yup.string(),
  zipCode: yup.number().typeError("Must be a number"),
});

interface EditFieldsProps {
  clientId: string;
  guarantor: any;
  teamMembersList: Array<{
    value: number;
    label: string;
    tableName: string;
    relationshipToClient?: string;
    relationship?: string;
  }>;
  onSave: (values: any) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * EditFields component provides a form for editing financial guarantor information.
 */
const EditFields = forwardRef<EditFieldsHandle, EditFieldsProps>(function EditFields(
  { clientId, guarantor, teamMembersList, onSave, onDirtyChange },
  ref
) {
  const formikRef = useRef<FormikProps<any>>(null);

  useImperativeHandle(ref, () => ({
    submit: () => formikRef.current?.handleSubmit(),
  }));

  const userInsurance: any = useSelector(selectors.getUserInsurance);

  // Find primary insurance
  const primaryInsurance = React.useMemo(() => {
    return userInsurance?.find((insurance: any) => insurance.is_primary && insurance.active);
  }, [userInsurance]);

  const fillFormValues = React.useCallback(
    (data: any) => {
      const selectedTeamMember =
        data?.guarantorTableId !== undefined && data?.guarantorTableId !== null
          ? teamMembersList?.find((member) => member.value === Number(data.guarantorTableId))
          : null;

      const relationshipToClient =
        data?.relationshipToClient ??
        data?.relationship ??
        selectedTeamMember?.relationshipToClient ??
        selectedTeamMember?.relationship;
      const phone = data?.phone ?? data?.phoneNumber;

      return {
        teamMemberId: data?.guarantorTableId ?? (data?.id ? "addNewGuarantor" : ""),
        firstName: data?.firstName ?? "",
        lastName: data?.lastName ?? "",
        dateOfBirth: data?.dateOfBirth ? new Date(dateWithoutTimezone(data.dateOfBirth)) : null,
        gender: data?.gender ?? "",
        relationshipToClient: relationshipToClient ?? "",
        phone: phone ?? "",
        email: data?.email ?? "",
        address1: data?.address1 ?? data?.address ?? "",
        address2: data?.address2 ?? "",
        country: data?.country ?? "US",
        state: data?.state ?? "",
        city: data?.city ?? "",
        zipCode: data?.zipCode ?? data?.zip ?? "",
        // State for the Financial Guarantor data is the same as the Insurance Subscriber
        isPrimarySubscriber: data?.isPrimarySubscriber ?? false,
        // Determines whether a guarantor must be created
        isCreating: !guarantor?.id,
        // TableId of the guarantor if team member is selected
        guarantorTableId: data?.guarantorTableId ?? null,
        guarantorTableName: data?.guarantorTableName ?? null,
      };
    },
    [guarantor, teamMembersList]
  );

  const initialValues = fillFormValues(guarantor);

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        onSave(values);
      }}
    >
      {({ touched, errors, handleBlur, handleChange, values, setFieldValue, setValues, dirty }) => {
        const isTeamMemberSelected =
          values.teamMemberId !== "" && values.teamMemberId !== "addNewGuarantor";

        const handleTeamMembersChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
          const teamMemberValue = event.target.value;

          // Find the selected member data from teamMembersList
          const selectedMember = teamMembersList?.find(
            (member) => member.value === Number(teamMemberValue)
          );

          if (selectedMember) {
            const selectedMemberData = {
              ...selectedMember,
              guarantorTableId: selectedMember?.value,
              guarantorTableName: selectedMember?.tableName,
              teamMemberId: selectedMember?.value,
            };

            setValues(fillFormValues(selectedMemberData));
          } else {
            // Clear form when no team member is selected
            setValues(fillFormValues(null));
            setFieldValue("teamMemberId", teamMemberValue);
          }
        };

        const handlePrimarySubscriberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const checked = event.target.checked;

          if (checked && primaryInsurance) {
            // Fill form with primary insurance subscriber data
            const subscriberData = {
              firstName: primaryInsurance.subscriber_first_name,
              lastName: primaryInsurance.subscriber_last_name,
              dateOfBirth: primaryInsurance.subscriber_dob,
              gender: primaryInsurance.subscriber_gender,
              email: primaryInsurance.subscriber_email,
              phone: primaryInsurance.subscriber_phone,
              relationshipToClient: primaryInsurance.subscriber_relationship,
              country: primaryInsurance.subscriber_country,
              state: primaryInsurance.subscriber_state,
              city: primaryInsurance.subscriber_city,
              zipCode: primaryInsurance.subscriber_zip,
              address1: primaryInsurance.subscriber_address,
              address2: primaryInsurance.subscriber_address2,
              isPrimarySubscriber: true,
            };
            setValues(fillFormValues(subscriberData));
          } else {
            // Clear form and explicitly set isPrimarySubscriber to false
            const resetData = {
              ...guarantor,
              isPrimarySubscriber: false,
            };
            setValues(fillFormValues(resetData));
          }
        };

        const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const phone = e.target.value.replace(/[^\d]/g, "");
          handleChange({ target: { name: "phone", value: phone } });
        };

        return (
          <>
            <DirtyTracker dirty={dirty} onDirtyChange={onDirtyChange} />
            <Grid container columnSpacing={5} rowSpacing={7} width="100%">
            <Grid xs={12}>
              <FormControl>
                <FormControlLabel
                  sx={{ alignItems: "flex-start" }}
                  control={
                    <Checkbox
                      name="isPrimarySubscriber"
                      checked={values.isPrimarySubscriber}
                      onChange={handlePrimarySubscriberChange}
                      disabled={!primaryInsurance}
                    />
                  }
                  label="Same as primary insurance subscriber"
                />
              </FormControl>
            </Grid>

            {!values.isPrimarySubscriber && (
              <>
                <Grid xs={12}>
                  <TextField
                    name="teamMemberId"
                    label="Use team member as financial guarantor"
                    select
                    onChange={handleTeamMembersChange}
                    value={values.teamMemberId}
                    fullWidth
                  >
                    <MenuItem value={""}>Select team member</MenuItem>
                    <MenuItem value={"addNewGuarantor"}>Add New Guarantor</MenuItem>
                    {teamMembersList?.map((member) => (
                      <MenuItem key={member.value} value={member.value}>
                        {member.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {values.teamMemberId !== "" && (
                  <>
                    <Grid
                      direction="row"
                      gap={3}
                      sx={{
                        display: values.teamMemberId !== "addNewGuarantor" ? "block" : "none",
                      }}
                    >
                      <EditUserInfoAlert clientId={clientId} />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="Guarantor's first name"
                        name="firstName"
                        error={Boolean(touched.firstName && errors.firstName)}
                        helperText={touched.firstName && (errors.firstName as string)}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.firstName}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="Guarantor's last name"
                        name="lastName"
                        error={Boolean(touched.lastName && errors.lastName)}
                        helperText={touched.lastName && (errors.lastName as string)}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.lastName}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <ANDatePicker
                        id="dateOfBirth"
                        name="dateOfBirth"
                        label="Guarantor's date of birth"
                        value={values.dateOfBirth}
                        onChange={(value: Date) => setFieldValue("dateOfBirth", value, true)}
                        format="PP"
                        sx={{ width: "100%" }}
                        disableFuture
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        name="gender"
                        label="Guarantor's gender"
                        select
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.gender}
                        fullWidth
                        disabled={isTeamMemberSelected}
                      >
                        <MenuItem disabled value={""} />
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Non-binary">Non-binary</MenuItem>
                        <MenuItem value="Gender Fluid">Gender Fluid</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="Guarantor's email address"
                        name="email"
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && (errors.email as string)}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.email}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="Guarantor's phone number"
                        name="phone"
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && (errors.phone as string)}
                        onBlur={handleBlur}
                        onChange={handlePhoneNumberChange}
                        value={formatToPhoneNumber(values.phone)}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Relationship to client"
                        name="relationshipToClient"
                        error={touched.relationshipToClient && !!errors.relationshipToClient}
                        helperText={touched.relationshipToClient && (errors.relationshipToClient as string)}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.relationshipToClient}
                        disabled={isTeamMemberSelected}
                      >
                        <MenuItem disabled value="">
                          relationship
                        </MenuItem>
                        {Object.entries(RELATIONSHIPS).map(([key, value]) => {
                          return (
                            <MenuItem key={key} value={key}>
                              {value}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Country"
                        name="country"
                        error={touched.country && !!errors.country}
                        helperText={touched.country && (errors.country as string)}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.country}
                        disabled={isTeamMemberSelected}
                      >
                        <MenuItem disabled value={""}>
                          Select
                        </MenuItem>
                        {Object.entries(COUNTRIES).map(([key, value]) => {
                          return (
                            <MenuItem key={key} value={key}>
                              {value}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="State"
                        name="state"
                        error={touched.state && !!errors.state}
                        helperText={touched.state && (errors.state as string)}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.state}
                        disabled={values.country !== "US" || isTeamMemberSelected}
                      >
                        <MenuItem disabled value={""}>
                          Select
                        </MenuItem>
                        {Object.entries(STATES).map(([key, value]) => {
                          return (
                            <MenuItem key={key} value={key}>
                              {value}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="City"
                        name="city"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.city}
                        error={touched.city && !!errors.city}
                        helperText={touched.city && (errors.city as string)}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="ZIP code"
                        name="zipCode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.zipCode}
                        error={touched.zipCode && !!errors.zipCode}
                        helperText={touched.zipCode && (errors.zipCode as string)}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="Guarantor's address"
                        name="address1"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address1}
                        error={touched.address1 && !!errors.address1}
                        helperText={touched.address1 && (errors.address1 as string)}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        label="Guarantor's address 2"
                        name="address2"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.address2}
                        error={touched.address2 && !!errors.address2}
                        helperText={touched.address2 && (errors.address2 as string)}
                        disabled={isTeamMemberSelected}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
          </>
        );
      }}
    </Formik>
  );
});

export default EditFields;
